import React, { useEffect, useState } from 'react'
import { leaV3API, RouteRow } from '../api/leaV3.api'

const UNIT_OPTIONS: Array<RouteRow['process_delay_unit']> = ['hours', 'calendar_days', 'working_days']
const TRIGGER_OPTIONS = ['', 'new_input', 'timeout']

export const RoutesView: React.FC = () => {
  const [routes, setRoutes] = useState<RouteRow[]>([])
  const [trigger, setTrigger] = useState('')
  const [category, setCategory] = useState('')
  const [loading, setLoading] = useState(true)
  const [savingId, setSavingId] = useState<string | null>(null)

  const load = () => {
    setLoading(true)
    leaV3API
      .getRoutes({ trigger: trigger || undefined, category: category || undefined })
      .then((r) => setRoutes(r.data))
      .finally(() => setLoading(false))
  }
  useEffect(load, [trigger, category])

  const update = async (id: string, value: number, unit: RouteRow['process_delay_unit']) => {
    setSavingId(id)
    try {
      await leaV3API.updateRoute(id, { process_delay_hours: value, process_delay_unit: unit })
      setRoutes((prev) =>
        prev.map((r) => (r.id === id ? { ...r, process_delay_hours: value, process_delay_unit: unit } : r))
      )
    } finally {
      setSavingId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center">
        <label className="text-sm text-gray-600">Trigger</label>
        <select className="border rounded px-2 py-1 text-sm" value={trigger} onChange={(e) => setTrigger(e.target.value)}>
          {TRIGGER_OPTIONS.map((t) => (
            <option key={t} value={t}>{t || 'all'}</option>
          ))}
        </select>
        <label className="text-sm text-gray-600">Catégorie</label>
        <input
          className="border rounded px-2 py-1 text-sm"
          placeholder="ex: rappel_paiement"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        />
        <span className="text-xs text-gray-500 ml-auto">{routes.length} routes</span>
      </div>

      {loading ? (
        <div className="p-4 text-gray-500">Chargement…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 text-xs">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="px-2 py-2 text-left">ID</th>
                <th className="px-2 py-2 text-left">Trigger</th>
                <th className="px-2 py-2 text-left">Status(es)</th>
                <th className="px-2 py-2 text-left">Catégorie</th>
                <th className="px-2 py-2 text-left">Sender</th>
                <th className="px-2 py-2 text-left">Msg type</th>
                <th className="px-2 py-2 text-left">Ladder slot</th>
                <th className="px-2 py-2 text-right">Delay</th>
                <th className="px-2 py-2 text-left">Unit</th>
                <th className="px-2 py-2 text-center">Auto</th>
                <th className="px-2 py-2 text-left">Template</th>
              </tr>
            </thead>
            <tbody>
              {routes.map((r) => (
                <RouteRowEditable key={r.id} route={r} onSave={update} saving={savingId === r.id} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const RouteRowEditable: React.FC<{
  route: RouteRow
  onSave: (id: string, value: number, unit: RouteRow['process_delay_unit']) => void
  saving: boolean
}> = ({ route, onSave, saving }) => {
  const [value, setValue] = useState(String(route.process_delay_hours))
  const [unit, setUnit] = useState<RouteRow['process_delay_unit']>(route.process_delay_unit)
  const dirty = String(route.process_delay_hours) !== value || route.process_delay_unit !== unit

  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50">
      <td className="px-2 py-1 font-mono">{route.id}</td>
      <td className="px-2 py-1">{route.applies_to_trigger.join(',')}</td>
      <td className="px-2 py-1 max-w-xs truncate" title={route.applies_to_status.join(', ')}>
        {route.applies_to_status.join(', ') || '*'}
      </td>
      <td className="px-2 py-1">{route.applies_to_category.join(',') || '—'}</td>
      <td className="px-2 py-1">{route.applies_to_sender_role.join(',') || '—'}</td>
      <td className="px-2 py-1">{route.applies_to_message_type.join(',') || '—'}</td>
      <td className="px-2 py-1 font-mono text-gray-600">{route.timeout_reason_pattern || '—'}</td>
      <td className="px-2 py-1 text-right">
        <input
          type="number"
          step="0.5"
          className="border rounded px-1 py-0.5 w-16 text-right"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </td>
      <td className="px-2 py-1">
        <select className="border rounded px-1 py-0.5" value={unit} onChange={(e) => setUnit(e.target.value as RouteRow['process_delay_unit'])}>
          {UNIT_OPTIONS.map((u) => (
            <option key={u} value={u}>{u}</option>
          ))}
        </select>
        {dirty && (
          <button
            disabled={saving}
            onClick={() => onSave(route.id, parseFloat(value), unit)}
            className="ml-2 px-2 py-0.5 bg-[#ef5343] text-white rounded text-xs disabled:opacity-50"
          >
            {saving ? '…' : 'Save'}
          </button>
        )}
      </td>
      <td className="px-2 py-1 text-center">{route.auto_execute ? '✓' : ''}</td>
      <td className="px-2 py-1 text-gray-500">{route.must_use_template || '—'}</td>
    </tr>
  )
}
